import type { ApiSeasonStat } from "../../../../api";

/**
 * Display rules for the season-stat tiles on the player profile.
 *
 * The backend sends measurements. Everything about how a tile READS is decided
 * here: rounding, the empty value, and the label text. That is the point of
 * P3-135 -- `player.season_stats` is a stored column, so a display rule left in
 * an adapter only reaches a fan when that player's row is next rewritten, which
 * between seasons can be months.
 *
 * See `docs/season-stats.md`.
 */

/** An absent measurement. Not a hyphen: the em dash reads as "no value". */
const EM_DASH = "—";

/**
 * Label per code, overriding the one on the wire.
 *
 * The three-column grid uses `repeat(3, minmax(max-content, 1fr))`, so a long
 * label widens its column instead of truncating and can overflow the `size="sm"`
 * dialog. EVERY label here is 8 characters or fewer. That budget belongs with
 * the grid, which is here, not in three adapters that cannot see it.
 *
 * A code missing from this table falls back to the label the backend sent, so a
 * new stat still ships without a frontend deploy.
 *
 * A code names ONE measurement across every sport. `INTERCEPTIONS_THROWN` and
 * `INTERCEPTIONS` are separate codes for that reason, though both are
 * "interceptions" in English. If a code you want is taken and your label
 * differs, your code is wrong: name the measurement, do not reuse the word.
 *
 * RETIRED -- `POINTS` and `SAVES` each named two different measurements and
 * were split (see below). They must never be reused, and they must stay out of
 * this table: a row stored before the split still carries them, and the
 * fallback to the wire label is what keeps that row rendering correctly until
 * the next refresh rewrites it.
 */
export const SEASON_STAT_LABELS: Record<string, string> = {
  // NFL
  PASSING_YARDS: "Pass Yds",
  PASSING_TOUCHDOWNS: "Pass TD",
  INTERCEPTIONS_THROWN: "INT",
  COMPLETION_PCT: "CMP%",
  PASSER_RATING: "RTG",
  // A kicker's total points scored. NHL's skater points is a different
  // measurement under a different code.
  POINTS_SCORED: "PTS",
  RUSHING_YARDS: "Rush Yds",
  RUSHING_TOUCHDOWNS: "Rush TD",
  RUSHING_ATTEMPTS: "ATT",
  YARDS_PER_CARRY: "YPC",
  RECEPTIONS: "REC",
  RECEIVING_YARDS: "Rec Yds",
  RECEIVING_TOUCHDOWNS: "Rec TD",
  YARDS_PER_RECEPTION: "YPR",
  LONG_RECEPTION: "LNG",
  TARGETS: "TGT",
  TOTAL_TACKLES: "TCKL",
  SOLO_TACKLES: "SOLO",
  SACKS: "SACK",
  INTERCEPTIONS: "INT",
  PASSES_DEFENSED: "PD",
  FORCED_FUMBLES: "FF",
  FIELD_GOALS: "FG",
  FIELD_GOAL_PCT: "FG%",
  LONG_FIELD_GOAL: "LNG",
  EXTRA_POINTS: "XP",
  FIELD_GOALS_50_PLUS: "50+",
  PUNTS: "Punts",
  PUNT_AVG: "AVG",
  PUNT_NET_AVG: "NET",
  LONG_PUNT: "LNG",
  PUNTS_INSIDE_20: "In 20",
  TOUCHBACKS: "TB",
  // MLB
  BATTING_AVG: "AVG",
  HOME_RUNS: "HR",
  RUNS_BATTED_IN: "RBI",
  RUNS: "R",
  ON_BASE_PERCENTAGE: "OBP",
  HITS: "H",
  WINS_LOSSES: "W-L",
  EARNED_RUN_AVG: "ERA",
  STRIKEOUTS: "K",
  WHIP: "WHIP",
  INNINGS_PITCHED: "IP",
  // A relief pitcher's credit for finishing a game. NHL's goalie saves is a
  // different measurement under a different code.
  PITCHER_SAVES: "SV",
  // NHL
  GOALS_AGAINST_AVG: "GAA",
  SAVE_PCT: "SV%",
  WINS: "W",
  SHUTOUTS: "SO",
  GAMES_PLAYED: "GP",
  GOALS: "G",
  ASSISTS: "A",
  PLUS_MINUS: "+/-",
  POWER_PLAY_GOALS: "PPG",
  SHOTS_ON_GOAL: "SOG",
  // Goals plus assists, which is what hockey means by "points".
  SKATER_POINTS: "P",
  // Shots against minus goals against. MLB's pitcher saves is a credit for
  // finishing a game, an unrelated measurement under its own code.
  GOALIE_SAVES: "Saves",
};

/**
 * The separator between the two halves of a `ratio`, per code.
 *
 * A made-of-attempts pair takes a slash: a kicker went 24/27. A pitcher's
 * record is not made of attempts, and baseball writes it 12-7, so that one code
 * takes a hyphen. A code missing here takes the slash.
 */
const RATIO_SEPARATORS: Record<string, string> = {
  WINS_LOSSES: "-",
};

/**
 * How many decimals a rate shows, and whether it drops its leading zero.
 *
 * A rate under 1 that a fan reads as a proportion is written `.287`, never
 * `0.287`. That is the convention for a batting average, an on-base percentage
 * and a save percentage. A goals-against average is a count per game, so it
 * keeps two decimals and any leading digit.
 *
 * A rate code missing from this table gets one decimal with its leading zero,
 * which suits every percentage, rating and per-attempt average the adapters
 * send today.
 */
const RATE_STYLES: Record<
  string,
  { decimals: number; dropLeadingZero?: boolean }
> = {
  BATTING_AVG: { decimals: 3, dropLeadingZero: true },
  ON_BASE_PERCENTAGE: { decimals: 3, dropLeadingZero: true },
  SAVE_PCT: { decimals: 3, dropLeadingZero: true },
  GOALS_AGAINST_AVG: { decimals: 2 },
  /*
   * An earned run average and a WHIP both carry two decimals, the way every
   * scoreboard writes them. Innings pitched takes the one-decimal default, and
   * that decimal is not a tenth: baseball writes two outs as `.2`, so `174.2`
   * is 174 innings and two outs.
   */
  EARNED_RUN_AVG: { decimals: 2 },
  WHIP: { decimals: 2 },
};

const DEFAULT_RATE_STYLE = { decimals: 1, dropLeadingZero: false };

const formatRate = (code: string, value: number): string => {
  const { decimals, dropLeadingZero } = RATE_STYLES[code] ?? DEFAULT_RATE_STYLE;
  const text = value.toFixed(decimals);
  return dropLeadingZero ? text.replace(/^(-?)0\./, "$1.") : text;
};

/** The label to show, preferring this app's text over the backend's. */
export const seasonStatLabel = (stat: ApiSeasonStat): string =>
  SEASON_STAT_LABELS[stat.code] ?? stat.label;

/**
 * The value to show.
 *
 * A `null` measurement means the feed said nothing:
 *
 *   - a count renders `0`, because "no tackles recorded" is zero tackles;
 *   - a rate renders an em dash, because an average over zero attempts is not
 *     `.000`, it is nothing;
 *   - a ratio renders an em dash, because a kicker who has not kicked has not
 *     missed every attempt.
 */
export const formatSeasonStatValue = (stat: ApiSeasonStat): string => {
  if (stat.format === "ratio") {
    const parts = stat.parts;
    if (!parts) return EM_DASH;
    const separator = RATIO_SEPARATORS[stat.code] ?? "/";
    return `${parts.made}${separator}${parts.attempts}`;
  }

  if (stat.value === null || !Number.isFinite(stat.value)) {
    return stat.format === "count" ? "0" : EM_DASH;
  }

  return stat.format === "count"
    ? String(Math.round(stat.value))
    : formatRate(stat.code, stat.value);
};
